import React, {useEffect, useRef, useState} from 'react'
import jsQR from 'jsqr'
import styled from 'styled-components'
import {Close} from '@mui/icons-material'
import {currentMedia} from '../../../styledComponents/ResponsiveConvenience'

export interface QRCodeScannerProps {
    descriptions?: string[]
    pause?: boolean
    onScan: (text: string) => void
    onClose: () => void
    onError?: (e: any) => void
}

const RADIUS = 16

export const QRCodeScanner = (props: QRCodeScannerProps) => {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const maskCanvasRef = useRef<HTMLCanvasElement | null>(null)
    const videoCanvasRef = useRef<HTMLCanvasElement | null>(null)
    const scannerRef = useRef<HTMLDivElement | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const pauseRef = useRef<boolean>(false)
    const [maskReady, setMaskReady] = useState<boolean>(false)

    const scannerAreaSizeRef = useRef({
        sx: 0,
        sy: 0,
        sw: 0,
        sh: 0,
    })

    const drawMask = () => {
        const maskCanvas = maskCanvasRef.current
        const videoCanvas = videoCanvasRef.current

        if (!maskCanvas || !videoCanvas) {
            return
        }

        const maskCtx = maskCanvas.getContext('2d')
        if (!maskCtx) {
            return
        }
        maskCanvas.width = videoCanvas.width
        maskCanvas.height = videoCanvas.height

        if ('roundRect' in maskCtx) {
            // roundRect対応ブラウザのみマスク表示(非対応のブラウザはかなり古いため配慮しない)
            maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height)
            maskCtx.fillStyle = 'rgba(0, 0, 0, 0.6)'
            maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height)

            const {sx, sy, sw, sh} = scannerAreaSizeRef.current
            const dpr = window.devicePixelRatio || 1

            maskCtx.globalCompositeOperation = 'destination-out'
            const radius = Math.min(sw, sh) / 2 > RADIUS * dpr ? RADIUS * dpr : Math.min(sw, sh) / 2
            maskCtx.roundRect(sx, sy, sw, sh, radius)
            maskCtx.fill()
        }

        setMaskReady(true)
    }

    const syncCanvasSize = (canvas: HTMLCanvasElement) => {
        const rect = canvas.getBoundingClientRect()
        const dpr = window.devicePixelRatio || 1
        canvas.width = Math.round(rect.width * dpr)
        canvas.height = Math.round(rect.height * dpr)
        return {dpr, cw: canvas.width, ch: canvas.height}
    }

    // カメラの初期化
    useEffect(() => {
        const initCamera = async () => {
            try {
                const mediaStream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        facingMode: 'environment', // 外カメを優先
                    },
                })

                streamRef.current = mediaStream
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream
                    await videoRef.current.play()
                }
            } catch (e) {
                if (props.onError) {
                    props.onError(e)
                }
                return
            }
        }

        initCamera()

        return () => {
            streamRef.current?.getTracks().forEach((track) => track.stop())
        }
    }, [])

    useEffect(() => {
        const resizeHandler = () => {
            // カメラの縦横切り替えなど、リサイズ時にマスクが遅れて見えるのを防止するため、マスク描画までVideoを隠す
            setMaskReady(false)
        }
        window.addEventListener('resize', resizeHandler)

        return () => {
            window.removeEventListener('resize', resizeHandler)
        }
    }, [])

    useEffect(() => {
        const interval = window.setInterval(() => {
            if (pauseRef.current) {
                return
            }

            const video = videoRef.current
            const videoCanvas = videoCanvasRef.current
            const scanner = scannerRef.current
            if (!video || !videoCanvas || !scanner) {
                return
            }

            const ctx = videoCanvas.getContext('2d', {willReadFrequently: true})
            if (!ctx) {
                return
            }

            const {dpr, cw, ch} = syncCanvasSize(videoCanvas)
            ctx.setTransform(1, 0, 0, 1, 0, 0)
            const vw = video.videoWidth
            const vh = video.videoHeight

            const scale = Math.max(cw / vw, ch / vh)

            const w = cw / scale
            const h = ch / scale
            const x = (vw - w) / 2
            const y = (vh - h) / 2

            ctx.drawImage(video, x, y, w, h, 0, 0, cw, ch)

            // リサイズ時は数段階サイズが変わることがあるが、リサイズ時のリスナーだと一度目しか検知できないため、ここでスキャナーサイズを計算
            const scannerRect = scanner.getBoundingClientRect()
            const canvasRect = videoCanvas.getBoundingClientRect()

            const sx = Math.round((scannerRect.left - canvasRect.left) * dpr)
            const sy = Math.round((scannerRect.top - canvasRect.top) * dpr)
            const sw = Math.round(scannerRect.width * dpr)
            const sh = Math.round(scannerRect.height * dpr)
            if (sw === 0 || sh === 0) {
                return
            }

            scannerAreaSizeRef.current = ({
                sx,
                sy,
                sw,
                sh,
            })

            const imageData = ctx.getImageData(sx, sy, sw, sh)
            const code = jsQR(imageData.data, Math.floor(sw), Math.floor(sh))

            if (code) {
                props.onScan(code.data)
            }

            drawMask()
        }, 100)

        return () => {
            window.clearInterval(interval)
        }
    }, [props.onScan])

    useEffect(() => {
        pauseRef.current = props.pause ?? false
    }, [props.pause])

    return <>
        <Overlay>
            <Video ref={videoRef} show={maskReady} muted playsInline autoPlay/>
            <Header>
                <span>二次元コード読み取り</span>
                <CloseIcon onClick={props.onClose}/>
            </Header>
            {props.descriptions && props.descriptions.length > 0 && <Description>
                {props.descriptions.map((description, index) => <span key={index}>{description}</span>)}
            </Description>}
            <ScannerArea ref={scannerRef}>
                <CornerOverlay className={'tl'} radius={RADIUS}/>
                <CornerOverlay className={'tr'} radius={RADIUS}/>
                <CornerOverlay className={'bl'} radius={RADIUS}/>
                <CornerOverlay className={'br'} radius={RADIUS}/>
                <QrOverlay src={'/resources/images/qrCodeScanner/qr_sample.svg'}/>
            </ScannerArea>
            <MaskCanvas ref={maskCanvasRef}/>
            <VideoCanvas ref={videoCanvasRef}/>
        </Overlay>
    </>
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1000; // メッセージダイアログより低い値(汎用性向上のために工夫要)
    background-color: black;
    display: flex;
    flex-direction: column;
`

// カメラデータを読み込む領域
const Video = styled.video<{ show: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover; // 変更する場合はScannerAreaの計算式も変えること
    z-index: -1;
    opacity: ${props => props.show ? 1 : 0};
`

// 静止画データを取得するためのカメラデータをマッピングする領域
const VideoCanvas = styled.canvas`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    opacity: 0;
`

const Header = styled.div`
    top: 0;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    color: white;
    padding: 16px;
    font-size: 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    user-select: none;
`
const CloseIcon = styled(Close)`
    font-size: 22px;
    cursor: pointer;
`

const Description = styled.div`
    display: flex;
    flex-direction: column;
    color: white;
    background: rgba(0, 0, 0, 0.4);
    border-radius: 33px;
    font-size: 12px;
    line-height: 18px;
    padding: 6px 45px;
    -webkit-box-pack: center;
    justify-content: center;
    -webkit-box-align: center;
    align-items: center;
    margin: 23px auto 0;
    user-select: none;
`

const ScannerArea = styled.div`
    position: relative;
    width: 250px;
    height: 250px;
    margin: 30px auto;
    overflow: hidden;
    ${currentMedia.greaterThan('pc')`
    width: auto;
    height: 60%;
    max-width: 500px;
    max-height: 500px;
    aspect-ratio: 1/1;
  `}
`

const CornerOverlay = styled.div<{ radius: number }>`
    position: absolute;
    width: 40px;
    height: 40px;
    // 四隅の枠線の最大長
    max-width: 20%;
    max-height: 20%;
    border: 4px solid white;

    &.tl {
        top: 0;
        left: 0;
        border-right: none;
        border-bottom: none;
        border-top-left-radius: ${props => props.radius}px;
    }

    &.tr {
        top: 0;
        right: 0;
        border-left: none;
        border-bottom: none;
        border-top-right-radius: ${props => props.radius}px;
    }

    &.bl {
        bottom: 0;
        left: 0;
        border-right: none;
        border-top: none;
        border-bottom-left-radius: ${props => props.radius}px;
    }

    &.br {
        bottom: 0;
        right: 0;
        border-left: none;
        border-top: none;
        border-bottom-right-radius: ${props => props.radius}px;
    }
`

const QrOverlay = styled.img`
    position: relative;
    width: 50%;
    height: 50%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0.3;
`

const MaskCanvas = styled.canvas`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    pointer-events: none;
`
